import { getClient, sleep } from "@conectate/components/ct-helpers";
import { doc, getFirestore, updateDoc } from "firebase/firestore";

import apiv2 from "../../../api/v2/API.js";
import { xFetch } from "../../../api/v2/Fetchv2.js";
import { FSSchema } from "../../../api/v2/schemas/fs.schema.js";
import { MGST } from "../../../api/v2/schemas/mg.schema-types.js";
import { MGSchema, TestQuestion, tests_complete } from "../../../api/v2/schemas/mg.schema.js";
import { users_offline } from "../../../apps/offline.ct.gt/models/offline.schema.js";
import { ServerDate } from "../../../xconfig/ServerDate.js";
import { randomOrder, throwError, withTimeout } from "../../../xconfig/utils.js";
import { IInitialStatePerformTest, IStatePerformTestPut } from "../models/initial-state-test-response.js";

// Generated by https://quicktype.io

export interface SEC {
	brands: Brand[];
	mobile: boolean;
	platform: string;
}

export interface Brand {
	brand: string;
	version: string;
}

function getSECCH() {
	let secCh: { "sec-ch-ua": string; "sec-ch-ua-platform": string } = { "sec-ch-ua": "", "sec-ch-ua-platform": "" };
	// @ts-ignore
	let sec: SEC | undefined = navigator.userAgentData;
	if (sec && sec.brands?.length && sec.platform) {
		secCh["sec-ch-ua"] = sec.brands.map(_ => `"${_.brand}";v="${_.version}"`).join(",");
		secCh["sec-ch-ua-platform"] = sec.platform;
	} else {
		return;
	}
	let raw = secCh["sec-ch-ua"];
	if (!raw) return;
	let browser = "other";
	let browserVersion = 0;
	let brandVersion = 0;
	raw.split(",").forEach(_ => {
		let [k, v] = _.trim().split(";");
		v = v.replace(/"/g, "").substring(2);
		if (browserVersion == 0) {
			if (k.toLowerCase().includes("chromium")) {
				browser = "chrome";
				browserVersion = parseFloat(v);
			} else if (k.toLowerCase().includes("firefox")) {
				browser = "firefox";
				browserVersion = parseFloat(v);
			} else if (k.toLowerCase().includes("safari")) {
				browser = "safari";
				browserVersion = parseFloat(v);
			}
		}
		if (brandVersion == 0 && k.toLowerCase().includes("brand")) {
			brandVersion = parseFloat(v);
		}
	});
	let rawPlatform = secCh["sec-ch-ua-platform"]?.toLowerCase();
	let platform = "other";
	if (rawPlatform) {
		if (rawPlatform.includes("windows")) {
			platform = "windows";
		} else if (rawPlatform.includes("mac")) {
			platform = "mac";
		} else if (rawPlatform.includes("linux")) {
			platform = "linux";
		} else if (rawPlatform.includes("android")) {
			platform = "android";
		} else if (rawPlatform.includes("ios")) {
			platform = "ios";
		}
	}
	return {
		browser,
		browserVersion,
		brandVersion,
		platform
	};
}

export class HighPerformance {
	static instance: HighPerformance;
	public mode: "firestore" | "offline" | "local" = "firestore";
	public testComplete!: tests_complete & { extra?: any };

	static getInstance() {
		if (HighPerformance.instance == null) {
			ServerDate.sync();
			HighPerformance.instance = new HighPerformance();
		}
		return HighPerformance.instance;
	}
	private clone<T>(obj: T): T {
		return JSON.parse(JSON.stringify(obj));
	}
	private checkHasNextGroup(length: number, index: number, testType: MGST.test.type) {
		if (testType == MGST.test.type.CLEAVER) {
			return false;
		}
		if (index == -1) return false;
		return length != index + 1;
	}

	private async updateOne(docID: string, data: any): Promise<[void, Error | undefined]> {
		data.updated_at = new Date(ServerDate.ts() * 1000);
		window.app.localStorage.currentTest = { ...window.app.localStorage.currentTest, ...data };
		if (this.mode == "firestore") {
			let promise = await withTimeout(updateDoc(doc(getFirestore(), FSSchema.collections.assessments, docID), data), 13_000).go();
			// return withTimeout(FirestoreUpdateDocs.updateOne(docID, data), 6_000).go();
			return promise;
		}
		return [undefined, undefined];
	}

	/** Offline.
	 * Se usa el `quizzez-offline` para mostrar el listado de pruebas pendientes
	 * Se usar en `apiv2.test.getTestComplete` para obtener el test completo a realizar
	 */
	public toTestComplete(test: MGSchema.tests, user: users_offline) {
		function groupsToComplete(serverTest: MGSchema.tests, hp?: boolean) {
			// ---------------------- Seteo las resp del TestComplete ----------------------
			serverTest?.groups.forEach(group => {
				group.questions.forEach((question, questionIndex) => {
					// @ts-ignore
					if (question.rules == "" || !question.rules) {
						question.rules = undefined;
					}
					question.answers.forEach((answer, answerIndex) => {
						switch (question.type) {
							case MGST.test.groups.questions.types.RESPUESTAS_EXACTAS:
							case MGST.test.groups.questions.types.RESPUESTA_EXACTA:
								question.answers[answerIndex] = { ...answer, text: "", correct: serverTest?.type == MGST.test.type.CLEAVER ? undefined : false };
								break;
							case MGST.test.groups.questions.types.MULTI_YES_NO:
								question.answers[answerIndex] = { ...answer, correct: undefined };
								break;
							case MGST.test.groups.questions.types.COMPLETE_INPUT_V2:
								question.answers[answerIndex] = { ...answer, t21: { correct: "" } };
								break;
							default:
								question.answers[answerIndex] = { ...answer, correct: false };
								break;
						}
					});
					group.questions[questionIndex] = { ...question, solve: false };
				});
			});
			let groups: tests_complete["groups"] = [];
			// ----------------------------------------------------------------
			serverTest.groups.forEach(group => {
				groups.push({
					_id: group._id,
					title: group.title || "",
					correctAnswers: group.questions,
					complete: false
				});
			});
			return groups;
		}

		let preferences: MGST.test.preferences[] = [];
		let exclude = [MGST.test.preferences.WEBCAM_REQUIRED, MGST.test.preferences.HEADSHOT_REQUIRED, MGST.test.preferences.DPI_PHOTO, MGST.test.preferences.MONITOR];
		for (let i = 0; i < test.preferences.length; i++) {
			if (!exclude.includes(test.preferences[i])) {
				preferences.push(test.preferences[i]);
			}
		}
		let testInfo = user.tests.find(i => i.tid == test.tid);
		let completeTest: tests_complete = {
			/**
			 * Se utiliza `~` en el _id para que el frontend sepa que hay que usar la clase `HighPerformance`.
			 * Si no se usa `~` se asume que es un `CollectionTest` y usa el servidor
			 * La estructura del _id es `~<Id de la prueba><ID del usuario>`
			 */
			_id: `~${test._id.substring(1)}${user.uid}`,
			ts: Math.floor(Date.now() / 1000),
			/** ID del usuario a asignar el test */
			uid: user.uid,
			pgid: test.uid,
			tid: test.tid,
			v: test.v,
			title: test.type == 0 ? test.title : test.title, //'',
			// descrip: body.descrip,
			groups: groupsToComplete(test),
			complete: false,
			score: 0,
			blur_count: 0,
			blur_limit: test.blur_limit!,
			type: test.type,
			screenshots: [],
			headshots: [],
			assign_by: `-bot`,
			cert_name: testInfo?.cert_name,
			prerequisites: testInfo?.prerequisites || [],
			// schedule: test.schedule,
			limit_time: test.limit_time!,
			attempts: test.attempts || 0,
			tags: testInfo?.tags || [],
			preferences: preferences,
			// @ts-ignore
			extra: { finish_msg: test.finish_msg, instructions: test.instructions, terms: test.terms }
		};
		return completeTest;
	}

	public async getTestComplete(
		testComplete: tests_complete & { extra?: any },
		template: { groups: { descrip?: string }[] }
	): Promise<[IInitialStatePerformTest | undefined, xFetch.xError<null> | undefined]> {
		/* if (this.testComplete == null)  */
		this.testComplete = testComplete;
		if (testComplete == null) {
			return [, throwError(404)];
		}
		await ServerDate.sync();
		let current = ServerDate.ts();
		let preferences = testComplete.preferences || [];

		if (testComplete.complete) {
			console.log("Sync with server....");
			await this.updateOne(testComplete._id, { groups: testComplete.groups });
			await apiv2.test.setCompleteTest({
				_id: testComplete._id,
				testType: testComplete.type,
				pgid: testComplete.pgid,
				tid: testComplete.tid,
				v: testComplete.v,
				complete_msg: testComplete.complete_msg,
				timeover_details: testComplete.timeover_details
			});
			console.log("Wait 5s....");
			await sleep(5000);
			return [, throwError(13, "La prueba ya ha sido completada")];
		}
		if (testComplete.limit_time && testComplete.start_ts != null) {
			// Si ya se paso del tiempo de la prueba, vamos a ver si le quedan intentos
			if (current > testComplete.limit_time + testComplete.start_ts) {
				let attempts = testComplete.attempts || 0;
				// Si ya no le quedan intentos, finalizamos la prueba
				if (attempts <= 0) {
					await this.updateOne(testComplete._id, { complete: true, finish_ts: testComplete.limit_time + testComplete.start_ts, complete_msg: MGST.tests_complete.complete_msg.timeover });
					return [, throwError(401, "Tiempo de prueba finalizado")];
				} else {
					// Si le quedan intentos, reactivamos la prueba
					testComplete.start_ts = current;
					testComplete.complete = false;
					testComplete.attempts = attempts - 1;
					this.updateOne(testComplete._id, {
						complete: false,
						score: 0,
						modified_at: new Date(ServerDate.ts() * 1000),
						timeover_details: "reenable",
						attempts: attempts - 1,
						start_ts: current,
						finish_ts: null,
						complete_msg: "",
						blur_count: 0
					});
				}
				/* let min_startTS = current;
				let max_startTS = 0;
				for (let i = 0; i < testComplete.groups.length; i++) {
					for (let j = 0; j < testComplete.groups[i].correctAnswers.length; j++) {
						let answered_at = testComplete.groups[i].correctAnswers[j].answered_at;
						if (answered_at == undefined) continue;
						if (answered_at > max_startTS) {
							max_startTS = answered_at;
						}
						if (answered_at < min_startTS) {
							min_startTS = answered_at;
						}
					}
				}
				if (max_startTS == 0) {
					max_startTS = current;
				} else {
					let time_elapsed = max_startTS - min_startTS;
					if (time_elapsed > testComplete.limit_time) {
						this.updateOne(testComplete._id, { complete: true, finish_ts: max_startTS, complete_msg: MGST.tests_complete.complete_msg.timeover });
						return [, throwError(401, "Tiempo de prueba finalizado")];
					}
					if (time_elapsed > 30) {
						time_elapsed = time_elapsed - 30;
					}
					max_startTS = current - time_elapsed;
				}
				testComplete.start_ts = max_startTS; 
				this.updateOne(testComplete._id, { start_ts: max_startTS });*/
			}
		}

		if (testComplete.schedule) {
			if (current < testComplete.schedule.s) {
				return [, throwError(401, "Fuera de horario")];
			}
			if (current > testComplete.schedule.f) {
				return [, throwError(401, "Fuera de horario")];
			}
		}

		let totalQuestions = 0;
		let totalSolved = 0;
		let answers: TestQuestion[] = [];
		let skipped: TestQuestion[] = [];
		let groupIndex = -1;
		let isConditional = false;
		// more faster than map.filter.reduce
		for (let i = 0; i < testComplete!.groups.length; i++) {
			totalQuestions += testComplete!.groups[i].correctAnswers.length;
			// -------------------------------------------------
			const group = testComplete!.groups[i];
			if (groupIndex < 0 && !group.complete) {
				groupIndex = i;
			}
			// -------------------------------------------------
			for (let j = 0; j < group.correctAnswers.length; j++) {
				let ans = group.correctAnswers[j];
				if (ans.conditional) {
					isConditional = true;
				}
				if (ans.solve) {
					totalSolved++;
				}
				// -------------------------------------------------
				// unsolve
				else if (groupIndex == i) {
					if (ans.skip) skipped.push(ans);
					else answers.push(ans);
				}
				// -------------------------------------------------
			}
		}
		if (isConditional && answers.length > 0) {
			preferences = preferences.filter(i => i != MGST.test.preferences.RANDOM_QUESTIONS && i != MGST.test.preferences.SKIP_QUESTIONS);
			for (let i = 0; i < testComplete.groups.length; i++) {
				const group = testComplete.groups[i];
				for (let j = 0; j < group.correctAnswers.length; j++) {
					const question = group.correctAnswers[j];
					if (question.solve) {
						if (question.conditional) {
							for (let k = 0; k < testComplete.groups[i].correctAnswers[j].answers.length; k++) {
								const ansx = testComplete.groups[i].correctAnswers[j].answers[k];
								if (ansx.correct) {
									if (ansx.jump == null) {
										if (j + 1 < testComplete.groups[i].correctAnswers.length) {
											answers = [testComplete.groups[i].correctAnswers[j + 1]];
										} else {
											answers = [];
										}
									} else if (ansx.jump == -1) {
										//finalizar prueba
										answers = [];
									} else {
										//saltar a la pregunta
										let jumpTo = ansx.jump;
										if (jumpTo < testComplete.groups[i].correctAnswers.length) {
											answers = [testComplete.groups[i].correctAnswers[jumpTo]];
										} else {
											answers = [];
										}
									}
								}
							}
						} else {
							if (j + 1 < testComplete.groups[i].correctAnswers.length) {
								answers = [testComplete.groups[i].correctAnswers[j + 1]];
							} else {
								answers = [];
							}
						}
					}
				}
			}
		}

		if (answers.length == 0 && skipped.length > 0) {
			answers = skipped;
		} else if (testComplete.preferences?.includes(MGST.test.preferences.RANDOM_QUESTIONS)) {
			answers = randomOrder(answers);
		}

		let next_question: TestQuestion | undefined = answers[0];
		let has_next_group = this.checkHasNextGroup(testComplete!.groups.length, groupIndex, testComplete.type);

		if (next_question == null && has_next_group == false) {
			return [
				{
					has_next_group: has_next_group,
					solved: totalSolved,
					test_type: testComplete.type,
					total: totalQuestions
				},
				undefined
			];
		}
		return [
			{
				has_next_group: has_next_group,
				solved: totalSolved,
				total: totalQuestions,
				test_title: testComplete.title,
				test_type: testComplete.type, // Cambio testComplete | testComplete
				preferences: testComplete!.preferences || [],
				grupo_title: testComplete.groups[groupIndex].title,
				tags: testComplete.tags,
				group: testComplete.group,
				hp: testComplete.hp,
				// @ts-ignore @hp
				grupo_descrip: template.groups[groupIndex].descrip,
				finish_msg: testComplete.extra?.finish_msg,
				instructions: testComplete.extra?.instructions,
				terms: testComplete.extra?.terms,
				next_question: next_question,
				// next_test: pendingTest,
				schedule: testComplete.schedule,
				limit_time: testComplete.limit_time,
				blur_limit: testComplete.blur_limit,
				start_ts: testComplete.start_ts
			} satisfies IInitialStatePerformTest,
			undefined
		];
	}

	public async startTest(_id: string, cameras?: { n: string; u: boolean }[]) {
		// Obtenemos ambos tests (TesComplete y CollectionTest)
		await ServerDate.sync();
		let now = ServerDate.ts();
		let testComplete = this.testComplete;
		if (testComplete?.start_ts == undefined) {
			let client = navigator.userAgent ? getClient(navigator.userAgent) : undefined;
			if (client && client.os == "windows" && client.osVersion == 10 && client.browserVersion > 89 && client.browser == "chrome") {
				let secCH = getSECCH();
				if (secCH && secCH.brandVersion > 13) {
					client.osVersion = 11;
				}
			}
			this.testComplete.client = client;
			this.testComplete.start_ts = now;
			this.testComplete.cameras = cameras;
			this.testComplete.complete_msg = MGST.tests_complete.complete_msg.doing;
			await this.updateOne(_id, this.clone({ client, start_ts: now, cameras, complete_msg: MGST.tests_complete.complete_msg.doing }));
			return {
				start_time: now,
				modifiedCount: 1
			};
		}
		return {
			start_time: testComplete?.start_ts,
			modifiedCount: 1
		};
	}

	public async addScreenshot(_id: string, params: { photo?: string; dpi?: string; headshots?: string[]; screenshot?: string; cam_stream?: string; screen_stream?: string }) {
		// Obtenemos ambos tests (TesComplete y CollectionTest)
		if (this.testComplete == null) return 0;
		let parcial: Partial<tests_complete> = {};
		if (params.dpi) {
			parcial.dpi = params.dpi;
		} else if (params.headshots) {
			parcial.headshots = params.headshots;
		} else if (params.screenshot) {
			this.testComplete.screenshots ||= [];
			parcial.screenshots = this.testComplete.screenshots;
			parcial.screenshots.push(params.screenshot);
		} else if (params.photo) {
			this.testComplete.photos ||= [];
			parcial.photos = this.testComplete.photos;
			parcial.photos.push(params.photo);
		} else if (params.cam_stream) {
			parcial.cam_stream = params.cam_stream;
		} else if (params.screen_stream) {
			parcial.screen_stream = params.screen_stream;
		}
		this.testComplete = this.clone({ ...this.testComplete, ...parcial });
		await this.updateOne(_id, this.clone({ ...this.testComplete, ...parcial }));
		return this.testComplete.screenshots.length;
	}

	public async skipQuestion(_id: string, question: TestQuestion) {
		// Obtenemos ambos tests (TesComplete y CollectionTest)
		this.testComplete?.groups.some(group =>
			group.correctAnswers.some(quest => {
				if (quest._id == question._id) {
					quest.skip = true;
					return true;
				}
			})
		);
		await this.updateOne(_id, this.clone({ groups: this.testComplete?.groups }));
	}

	public async blurTest(_id: string) {
		// Obtenemos ambos tests (TesComplete y CollectionTest)
		this.testComplete.blur_count++;
		await this.updateOne(_id, this.clone({ blur_count: this.testComplete.blur_count }));
		return this.testComplete.blur_count;
	}

	public async t0EvalNormal({ questionAnswered }: { questionAnswered: TestQuestion }): Promise<[IStatePerformTestPut, xFetch.xError<null> | undefined]> {
		let now = await this.getUnix();
		let gpIndex = -1;
		let qIndex = -1;
		if (this.testComplete.complete) {
			return [{ next_question: undefined, limit_time: this.testComplete.limit_time }, undefined];
		}

		this.testComplete.groups.some((group, groupIndex) => {
			if (group.complete == false) {
				gpIndex = groupIndex;
				for (let questionIndex = 0; questionIndex < group.correctAnswers.length; questionIndex++) {
					if (group.correctAnswers[questionIndex]._id == questionAnswered._id) {
						qIndex = questionIndex;
						// Seteo que ya resolvio la pregunta
						group.correctAnswers[questionIndex].solve = true;
						group.correctAnswers[questionIndex].answers = questionAnswered.answers;
						group.correctAnswers[questionIndex].answered_at = now;
						if (group.correctAnswers.every(i => i.solve)) {
							group.complete = true; // FIX!!
						}
						return true;
					}
				}
			}
		});
		if (gpIndex == -1 || qIndex == -1) return [{ next_question: undefined, limit_time: this.testComplete.limit_time }, undefined];

		// Le caigo encima a todos los grupos
		let [ok, err] = await this.updateOne(this.testComplete._id, this.clone({ groups: this.testComplete.groups }));
		if (err) {
			return [
				null as any,
				{
					status: 0,
					number: 0,
					error_msg: "InternetDisconnectedException",
					error_type: "0"
				}
			];
		}

		// ====================== Búsqueda de la proxima pregunta ======================

		let anwers: TestQuestion[] = [];
		let skiped: TestQuestion[] = [];
		let next_question: TestQuestion | undefined = undefined;
		let isConditional = false;

		for (let i = 0; i < this.testComplete.groups[gpIndex].correctAnswers.length; i++) {
			const ans = this.testComplete.groups[gpIndex].correctAnswers[i];
			if (ans.conditional) {
				isConditional = true;
			}
			if (!ans.solve) {
				if (ans.skip) skiped.push(ans);
				else anwers.push(ans);
			}
		}
		if (anwers.length > 0 && isConditional) {
			let xnext_question: TestQuestion | undefined = anwers[0];
			// Agregar AQUI
			for (let j = 0; j < this.testComplete.groups[gpIndex].correctAnswers.length; j++) {
				const question = this.testComplete.groups[gpIndex].correctAnswers[j];
				if (question.solve) {
					if (question.conditional) {
						for (let k = 0; k < this.testComplete.groups[gpIndex].correctAnswers[j].answers.length; k++) {
							const ansx = this.testComplete.groups[gpIndex].correctAnswers[j].answers[k];
							if (ansx.correct) {
								if (ansx.jump == null) {
									if (j + 1 < this.testComplete.groups[gpIndex].correctAnswers.length) {
										xnext_question = this.testComplete.groups[gpIndex].correctAnswers[j + 1];
									} else {
										xnext_question = undefined;
									}
								} else if (ansx.jump == -1) {
									//finalizar prueba
									xnext_question = undefined;
								} else {
									//saltar a la pregunta
									let jumpTo = ansx.jump;
									if (jumpTo < this.testComplete.groups[gpIndex].correctAnswers.length) {
										xnext_question = this.testComplete.groups[gpIndex].correctAnswers[jumpTo];
									} else {
										xnext_question = undefined;
									}
								}
							}
						}
					} else {
						if (j + 1 < this.testComplete.groups[gpIndex].correctAnswers.length) {
							xnext_question = this.testComplete.groups[gpIndex].correctAnswers[j + 1];
						} else {
							xnext_question = undefined;
						}
					}
				}
			}
			return [
				{
					next_question: xnext_question,
					limit_time: this.testComplete.limit_time
				},
				undefined
			];
		}
		if (anwers.length == 0) {
			anwers = skiped;
		} else if (this.testComplete.preferences?.includes(MGST.test.preferences.RANDOM_QUESTIONS)) {
			anwers = randomOrder(anwers);
		}
		console.log(
			"Grupo",
			gpIndex,
			"Pregunta index",
			qIndex,
			"pregunta ID",
			questionAnswered._id,
			this.testComplete.groups[gpIndex].correctAnswers[qIndex]._id,
			"Resueltas",
			this.testComplete.groups[gpIndex].correctAnswers.filter(i => i.solve).length,
			"Pendientes",
			this.testComplete.groups[gpIndex].correctAnswers.filter(i => !i.solve).length,
			"Total",
			this.testComplete.groups[gpIndex].correctAnswers.length
		);

		if (anwers.length > 0) next_question = anwers[0];

		// Check preferences
		return [{ next_question, limit_time: this.testComplete.limit_time }, undefined];
	}

	public async setCompleteTest(params: { _id: string; testType: number; pgid: string; tid: number; v: number; complete_msg?: MGST.tests_complete.complete_msg; timeover_details?: any }) {
		this.testComplete.complete = true;
		this.testComplete.finish_ts = await this.getUnix(true);
		this.testComplete.complete_msg = params.complete_msg;
		this.testComplete.timeover_details = params.timeover_details;
		let [tc, err] = await this.updateOne(params._id, this.clone({ complete: true, finish_ts: await this.getUnix(), complete_msg: params.complete_msg, timeover_details: params.timeover_details }));
		if (err) return;
		// Esto es por si la prueba es modalidad `OFFLINE`
		return this.testComplete;
	}

	public cleanTestComplete() {
		// @ts-ignore
		this.testComplete = undefined;
		window.app.localStorage.currentTest = undefined;
	}

	private async getUnix(force = true) {
		if (!ServerDate.synced || force) await ServerDate.sync();
		return ServerDate.ts();
	}
}
